import { useRef } from "react";
import { Modal, Row, Col, Button, Alert } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage, getIn } from "formik";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { showLoading, hideLoading } from "react-redux-loading-bar";
import PlaceholderImage from "../../../assets/Images/placeholder-image.png";
import { uploadFile } from "../../../utilities/ImageUpload";
import { getImageExtensions } from "../../../utilities/FUNCTIONS";

const styles = {
  headerContent: {
    paddingRight: "22px",
    width: "100%",
  },
  titleWrap: {
    alignItems: "center",
    display: "flex",
    gap: "12px",
  },
  titleIcon: {
    alignItems: "center",
    border: "2px solid #ff4f12",
    borderRadius: "8px",
    color: "#ff4f12",
    display: "inline-flex",
    fontSize: "18px",
    height: "36px",
    justifyContent: "center",
    width: "36px",
  },
  subtitle: {
    color: "#586270",
    fontSize: "13px",
    fontWeight: 600,
    margin: "2px 0 0",
  },
  badgeStrip: {
    display: "grid",
    gap: "10px",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    marginTop: "14px",
  },
  metaBadge: {
    alignItems: "center",
    backgroundColor: "#fff",
    border: "1px solid #edf0f3",
    borderRadius: "6px",
    boxShadow: "0 8px 20px rgba(18, 38, 63, 0.04)",
    display: "flex",
    gap: "8px",
    minHeight: "56px",
    maxWidth: "100%",
    overflow: "hidden",
    padding: "9px 10px",
  },
  metaBadgeIcon: {
    color: "#ff4f12",
    flex: "0 0 auto",
    fontSize: "19px",
    lineHeight: 1,
  },
  metaBadgeLabel: {
    color: "#5c6670",
    display: "block",
    fontSize: "10px",
    fontWeight: 700,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  metaBadgeValue: {
    color: "#263238",
    display: "block",
    fontSize: "12px",
    fontWeight: 600,
    lineHeight: 1.25,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  statusBadgeValue: {
    backgroundColor: "#dff2da",
    borderRadius: "12px",
    color: "#216b2a",
    display: "inline-block",
    fontSize: "11px",
    marginTop: "3px",
    padding: "2px 8px",
  },
  financerBadgeValue: {
    fontSize: "10px",
    fontWeight: 500,
    whiteSpace: "normal",
    wordBreak: "break-all",
  },
  modalBody: {
    backgroundColor: "#fbfcfe",
    padding: "18px",
  },
  mainGrid: {
    alignItems: "stretch",
    display: "grid",
    gap: "18px",
    gridTemplateColumns: "330px minmax(0, 1fr)",
  },
  complianceColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    minHeight: "100%",
  },
  detailsColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  section: {
    backgroundColor: "#fff",
    border: "1px solid #e7e9ee",
    borderRadius: "6px",
    padding: "14px",
    height: "100%",
  },
  businessComplianceSection: {
    flex: "1 1 auto",
  },
  sectionHeader: {
    alignItems: "center",
    borderBottom: "1px solid #edf0f3",
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "14px",
    paddingBottom: "9px",
  },
  sectionTitle: {
    color: "#263238",
    fontSize: "15px",
    fontWeight: 700,
    margin: 0,
  },
  sectionIcon: {
    color: "#ff4f12",
    fontSize: "17px",
    marginRight: "10px",
  },
  complianceCard: {
    backgroundColor: "#fbfcfd",
    border: "1px solid #e5e8ec",
    borderRadius: "6px",
    marginBottom: "10px",
    padding: "10px",
  },
  requirementTitle: {
    color: "#3a4651",
    display: "block",
    fontSize: "13px",
    fontWeight: 700,
    marginBottom: "8px",
  },
  imageTile: {
    display: "inline-flex",
    flexDirection: "column",
    margin: "0 8px 8px 0",
    width: "136px",
  },
  imageLink: {
    backgroundColor: "#fff",
    border: "1px solid #d8dde4",
    borderRadius: "6px",
    display: "block",
    overflow: "hidden",
  },
  image: {
    display: "block",
    height: "86px",
    objectFit: "cover",
    width: "134px",
  },
  fileThumbnail: {
    alignItems: "center",
    backgroundColor: "#f7f9fc",
    color: "#47525d",
    display: "flex",
    flexDirection: "column",
    height: "86px",
    justifyContent: "center",
    padding: "8px",
    textAlign: "center",
    width: "134px",
  },
  fileThumbnailIcon: {
    color: "#ff4f12",
    fontSize: "26px",
    lineHeight: 1,
    marginBottom: "6px",
  },
  fileThumbnailName: {
    display: "block",
    fontSize: "11px",
    fontWeight: 600,
    lineHeight: 1.2,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  imageActions: {
    alignItems: "center",
    display: "flex",
    gap: "4px",
    justifyContent: "center",
    marginTop: "5px",
  },
  iconButton: {
    alignItems: "center",
    border: "1px solid #d8dde4",
    borderRadius: "4px",
    color: "#47525d",
    display: "inline-flex",
    height: "28px",
    justifyContent: "center",
    padding: 0,
    width: "28px",
  },
  textValue: {
    fontSize: "13px",
    minHeight: "36px",
  },
};

const isBlank = (value) => value === undefined || value === null || value === "";

const getFirstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "") ?? "-";

const getDigits = (value) => String(value || "").replace(/\D/g, "");

const formatCnic = (value) => {
  const digits = getDigits(value).slice(0, 13);
  if (digits.length <= 5) return digits;
  if (digits.length <= 12) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12)}`;
};

const isRejectedStatus = (value) => String(value || "").toLowerCase() === "rejected";


const isImageFile = (url = "") => {
  const extension = url.split(".").pop().split("?")[0].toLowerCase();

  return [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "bmp",
    "webp",
    "svg",
    "jfif",
    "avif",
  ].includes(extension);
};

const getFileName = (url) => {
  if (!url) return "attachment";

  try {
    const pathname = new URL(url, window.location.origin).pathname;
    const name = decodeURIComponent(pathname.split("/").pop() || "");
    return name || "attachment";
  } catch {
    const parts = String(url).split("/");
    return decodeURIComponent(parts[parts.length - 1] || "attachment");
  }
};

const getFileIcon = (url = "") => {
  const extension = String(url).split(".").pop().split("?")[0].toLowerCase();

  if (extension === "pdf") return "fas fa-file-pdf";
  if (["xls", "xlsx", "csv"].includes(extension)) return "fas fa-file-excel";
  if (["doc", "docx"].includes(extension)) return "fas fa-file-word";
  return "fas fa-file-alt";
};

const AttachmentThumbnail = ({ value, label }) => {
  if (!value || isImageFile(value)) {
    return (
      <img
        className="kyc-form-img"
        style={styles.image}
        alt={label}
        src={value || PlaceholderImage}
        onError={(event) => {
          event.currentTarget.onerror = null;
          event.currentTarget.src = PlaceholderImage;
        }}
      />
    );
  }

  const fileName = getFileName(value);

  return (
    <span style={styles.fileThumbnail}>
      <i className={getFileIcon(value)} style={styles.fileThumbnailIcon}></i>
      <span style={styles.fileThumbnailName} title={fileName}>
        {fileName}
      </span>
    </span>
  );
};

// const validate = (values) => {
//   const errors = {};
//   const setError = (path, message) => {
//     const parts = path.split(".");
//     let current = errors;

//     parts.forEach((part, index) => {
//       if (index === parts.length - 1) current[part] = message;
//       else {
//         current[part] = current[part] || {};
//         current = current[part];
//       }
//     });
//   };

//   if (isBlank(values.vizshopId)) setError("vizshopId", "Vizshop ID is missing.");
//   if (isBlank(values.lenderId)) setError("lenderId", "Lender ID is missing.");
//   if (isBlank(values.userId)) setError("userId", "User ID is missing.");
//   if (isBlank(values.creditLimitRequired)) {
//     setError("creditLimitRequired", "Credit limit is required.");
//   } else if (Number.isNaN(Number(values.creditLimitRequired))) {
//     setError("creditLimitRequired", "Credit limit must be a number.");
//   }

//   const contactDigits = getDigits(values.personalDetails?.contactNumber);
//   if (!contactDigits) {
//     setError("personalDetails.contactNumber", "Contact number is required.");
//   } else if (contactDigits.length !== 11) {
//     setError("personalDetails.contactNumber", "Contact number must be 11 digits.");
//   }

//   const personalCnicDigits = getDigits(values.personalDetails?.cnic);
//   if (!personalCnicDigits) {
//     setError("personalDetails.cnic", "CNIC is required.");
//   } else if (personalCnicDigits.length !== 13) {
//     setError("personalDetails.cnic", "CNIC must be 13 digits.");
//   }

//   const businessCnicDigits = getDigits(values.businessDetails?.cnic);
//   if (values.businessDetails?.cnic && businessCnicDigits.length !== 13) {
//     setError("businessDetails.cnic", "CNIC must be 13 digits.");
//   }

//   const email = values.personalDetails?.email?.trim();
//   if (!email) setError("personalDetails.email", "Email is required.");
//   else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
//     setError("personalDetails.email", "Enter a valid email address.");
//   }

//   return errors;
// };

const FieldControl = ({ label, name, type = "text", readOnly }) => (
  <div className="form-group mb-3">
    <label htmlFor={name} className="mb-1 small font-weight-bold text-muted">{label}</label>
    <Field id={name} name={name} type={type} className="form-control form-control-sm" readOnly={readOnly} />
    <ErrorMessage name={name} component="div" className="text-danger small" />
  </div>
);

const ContactNumberField = ({ label, name, readOnly }) => (
  <div className="form-group mb-3">
    <label htmlFor={name} className="mb-1 small font-weight-bold text-muted">{label}</label>
    <Field name={name}>
      {({ field, form }) => (
        <input
          {...field}
          id={name}
          type="text"
          inputMode="numeric"
          maxLength={11}
          className="form-control form-control-sm"
          readOnly={readOnly}
          value={getDigits(field.value).slice(0, 11)}
          onChange={(event) => {
            form.setFieldValue(name, getDigits(event.target.value).slice(0, 11));
          }}
        />
      )}
    </Field>
    <ErrorMessage name={name} component="div" className="text-danger small" />
  </div>
);

const CnicField = ({ label, name, readOnly }) => (
  <div className="form-group mb-3">
    <label htmlFor={name} className="mb-1 small font-weight-bold text-muted">{label}</label>
    <Field name={name}>
      {({ field, form }) => (
        <input
          {...field}
          id={name}
          type="text"
          inputMode="numeric"
          maxLength={15}
          className="form-control form-control-sm"
          readOnly={readOnly}
          value={formatCnic(field.value)}
          onChange={(event) => {
            form.setFieldValue(name, formatCnic(event.target.value));
          }}
        />
      )}
    </Field>
    <ErrorMessage name={name} component="div" className="text-danger small" />
  </div>
);

const getSectionIcon = (title) => {
  if (/personal information/i.test(title)) return "fas fa-user-friends";
  if (/personal compliance/i.test(title)) return "far fa-user";
  if (/business information/i.test(title)) return "fas fa-briefcase";
  if (/business compliance/i.test(title)) return "fas fa-briefcase";
  if (/address/i.test(title)) return "fas fa-map-marker-alt";
  return "fas fa-shield-alt";
};

const SectionHeader = ({ title }) => (
  <div style={styles.sectionHeader}>
    <h5 style={styles.sectionTitle}>
      <i className={getSectionIcon(title)} style={styles.sectionIcon}></i>
      {title}
    </h5>
  </div>
);

const getBadgeIcon = (label) => {
  if (/viz/i.test(label)) return "far fa-address-card";
  if (/financer/i.test(label)) return "fas fa-university";
  if (/kyc/i.test(label)) return "fas fa-shield-alt";
  if (/agreement/i.test(label)) return "far fa-file-alt";
  if (/assign/i.test(label)) return "fas fa-user-friends";
  return "fas fa-info-circle";
};

const HeaderBadge = ({ label, value, isStatus, isWide }) => (
  <span style={styles.metaBadge} title={`${label}: ${value}`}>
    <i className={getBadgeIcon(label)} style={styles.metaBadgeIcon}></i>
    <span style={{ minWidth: 0 }}>
      <span style={styles.metaBadgeLabel}>{label}</span>
      <span
        style={{
          ...styles.metaBadgeValue,
          ...(isStatus ? styles.statusBadgeValue : {}),
          ...(isWide ? styles.financerBadgeValue : {}),
        }}
      >
        {value}
      </span>
    </span>
  </span>
);

const ModalHeaderBadges = ({ values }) => {
  const kycStatus = getFirstValue(values?.kycStatus);
  const badges = [
    {
      label: "VIZ ID",
      value: getFirstValue(values?.vizId),
    },
    {
      label: "Financer ID",
      value: getFirstValue(values?.financerId),
      isWide: true,
    },
    {
      label: "KYC Status",
      value: kycStatus,
      isStatus: true,
    },
    {
      label: "Agreement Status",
      value: getFirstValue(values?.contractStatus),
      isStatus: true,
    },
    {
      label: "Assign Limit",
      value: getFirstValue(values?.assignLimit),
    },
    {
      label: "Reason of Rejection",
      value: getFirstValue(values?.reason),
      show: isRejectedStatus(kycStatus),
      isWide: true,
    },
  ].filter((badge) => badge.show !== false);

  return (
    <div style={styles.badgeStrip}>
      {badges.map((badge) => (
        <HeaderBadge
          key={badge.label}
          label={badge.label}
          value={badge.value}
          isStatus={badge.isStatus}
          isWide={badge.isWide}
        />
      ))}
    </div>
  );
};

const formatRequirementName = (name) =>
  (name || "Compliance requirement")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const isUrl = (value) => /^https?:\/\//i.test(value || "");

const isImageRequirement = (detail, value) =>
  isUrl(value) ||
  detail?.inputType === "document" ||
  /image|picture|photo|cnic|front|back|document|attachment/i.test(
    detail?.requirementName || ""
  );

const hasAllowMultipleFilesTag = (detail) => {
  const tags = [detail?.tag, detail?.tags, detail?.Tag, detail?.Tags]
    .flat()
    .filter(Boolean)
    .map((tag) => String(tag).trim().toLowerCase());

  return (
    detail?.allowMultiImages === true ||
    detail?.allow_multi_images === true ||
    detail?.allowMultipleFiles === true ||
    detail?.allow_multiple_files === true ||
    tags.includes("allowmultiimages") ||
    tags.includes("allow_multi_images") ||
    tags.includes("allowmultiplefiles") ||
    tags.includes("allow_multiple_files")
  );
};

const normalizeAcceptedFormats = (formats = []) =>
  formats
    .map((format) => String(format || "").trim().replace(/^\./, "").toLowerCase())
    .filter(Boolean);

const getAcceptedFormats = (detail) => {
  const acceptedFormats = normalizeAcceptedFormats(
    detail?.acceptedFormats ||
      detail?.acceptsFormats ||
      detail?.accepts_formats ||
      detail?.validations?.accepts_formats
  );

  return acceptedFormats.length > 0 ? acceptedFormats : getImageExtensions();
};

const getFileAcceptAttribute = (acceptedFormats) =>
  normalizeAcceptedFormats(acceptedFormats)
    .map((format) => `.${format}`)
    .join(",");

const ComplianceImageControl = ({
  value,
  fieldName,
  label,
  readOnly,
  setFieldValue,
  allowMultiple,
  values = [],
  valuesFieldName,
  valueIndex,
  acceptedFormats,
}) => {
  const fileInputRef = useRef();
  const editFileInputRef = useRef();
  const editImageIndexRef = useRef(null);
  const dispatch = useDispatch();
  const allowedExtensions = getAcceptedFormats({ acceptedFormats });
  const acceptAttribute = getFileAcceptAttribute(allowedExtensions);

  const openUploadImage = () => {
    if (!readOnly) fileInputRef.current.click();
  };

  const openEditImage = (index) => {
    if (!readOnly) {
      editImageIndexRef.current = index;
      editFileInputRef.current.click();
    }
  };

  const removeImage = () => {
    if (allowMultiple && valuesFieldName) {
      setFieldValue(
        valuesFieldName,
        values.filter((_, index) => index !== valueIndex)
      );
      return;
    }

    setFieldValue(fieldName, "");
  };

  const saveImage = (event, isSingleImageEdit = false) => {
    const selectedFiles = Array.from(event.target.files || []);

    if (selectedFiles.length === 0) return;

    const hasInvalidFile = selectedFiles.some((file) => {
      const currentExtension = file.name.split(".").pop().toLowerCase();
      return !allowedExtensions.includes(currentExtension);
    });

    if (hasInvalidFile) {
      event.target.value = null;
      toast.error(`Selected invalid file extension. Allowed formats: ${allowedExtensions.join(", ")}`);
      return;
    }

    dispatch(showLoading());

    Promise.all(selectedFiles.map((file) => uploadFile(file)))
      .then((responses) => {
        if (valuesFieldName) {
          const existingValues = values.filter(
            (item) => item !== undefined && item !== null && String(item).trim() !== ""
          );

          if (isSingleImageEdit && editImageIndexRef.current !== null) {
            const nextValues = [...existingValues];
            if (responses.length === 1) {
              nextValues.splice(editImageIndexRef.current, 1, responses[0]);
            } else {
              nextValues.splice(editImageIndexRef.current + 1, 0, ...responses);
            }
            setFieldValue(valuesFieldName, nextValues);
            return;
          }

          setFieldValue(valuesFieldName, [...existingValues, ...responses]);
          return;
        }

        if (responses[0]) {
          const response = responses[0];
          setFieldValue(fieldName, response);
        }
      })
      .catch((error) => {
        toast.error(error);
      })
      .finally(() => {
        dispatch(hideLoading());
        editImageIndexRef.current = null;
        event.target.value = null;
      });
  };

  if (valuesFieldName) {
    const visibleValues = values.filter(
      (item) => item !== undefined && item !== null && String(item).trim() !== ""
    );
    const displayValues = visibleValues.length > 0 ? visibleValues : [""];

    return (
      <div style={{ ...styles.imageTile, width: "100%" }}>
       <div
  style={{
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
    alignItems: "flex-start",
  }}
>
          {displayValues.map((item, index) => (
            <div key={`${valuesFieldName}.${index}`} style={{ margin: "0 8px 8px 0", width: "136px" }}>
              <a
                href={item || PlaceholderImage}
                target="_blank"
                rel="noopener noreferrer"
                title="Open attachment"
                style={styles.imageLink}
              >
                <AttachmentThumbnail value={item} label={label} />
              </a>
              {!readOnly && (
                <div style={styles.imageActions}>
                  <button
                    type="button"
                    onClick={() => openEditImage(index)}
                    className="btn btn-sm bg-white"
                    style={styles.iconButton}
                    title="Edit image"
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const nextValues = visibleValues.filter((_, valueIndex) => valueIndex !== index);
                      setFieldValue(valuesFieldName, nextValues);
                    }}
                    className="btn btn-sm bg-white"
                    style={styles.iconButton}
                    title="Delete image"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
        <input
          ref={editFileInputRef}
          hidden
          type="file"
          name="imageFile"
          accept={acceptAttribute}
          multiple={allowMultiple}
          onChange={(event) => saveImage(event, true)}
        />
      </div>
    );
  }

  return (
    <div style={styles.imageTile}>
      <div>
        <a
          href={value || PlaceholderImage}
          target="_blank"
          rel="noopener noreferrer"
          title="Open attachment"
          style={styles.imageLink}
        >
          <AttachmentThumbnail value={value} label={label} />
        </a>
        <input
          ref={fileInputRef}
          hidden
          type="file"
          name="imageFile"
          accept={acceptAttribute}
          multiple={allowMultiple}
          onChange={saveImage}
        />
      </div>
      {!readOnly && (
        <div style={styles.imageActions}>
          <button type="button" onClick={openUploadImage} className="btn btn-sm bg-white" style={styles.iconButton} title="Edit image">
            <i className="fas fa-edit"></i>
          </button>
          <button type="button" onClick={removeImage} className="btn btn-sm bg-white" style={styles.iconButton} title="Delete image">
            <i className="fas fa-trash"></i>
          </button>
        </div>
      )}
    </div>
  );
};

const ComplianceSection = ({ name, details, isFieldReadOnly, setFieldValue }) => {
  if (!Array.isArray(details) || details.length === 0) return null;

  return (
    <Row>
      {details.map((detail, detailIndex) => {
        const values = Array.isArray(detail.values) ? detail.values : [];
        const renderedValues = values.length > 0 ? values : [""];
        const valuesFieldName = `${name}.${detailIndex}.values`;
        const requirementLabel =
          detail.displayLabel || formatRequirementName(detail.requirementName);
        const isImageOnlyRequirement =
          renderedValues.length > 0 && renderedValues.every((value) => isImageRequirement(detail, value));
        const allowMultipleImages = hasAllowMultipleFilesTag(detail);
        const acceptedFormats = getAcceptedFormats(detail);
        const isRequirementReadOnly = !renderedValues.some((_, valueIndex) =>
          !isFieldReadOnly(`${name}.${detailIndex}.values.${valueIndex}`)
        );

       if (isImageOnlyRequirement && renderedValues.length > 1) {
          return (
            <Col xs={12} key={valuesFieldName} className="mb-2">
              <div style={{ ...styles.complianceCard, height: "100%" }}>
                <strong style={styles.requirementTitle}>
                  {requirementLabel}
                </strong>

                <ComplianceImageControl
                  label={requirementLabel}
                  readOnly={isRequirementReadOnly}
                  setFieldValue={setFieldValue}
                  allowMultiple={allowMultipleImages}
                  values={renderedValues}
                  valuesFieldName={valuesFieldName}
                  acceptedFormats={acceptedFormats}
                />
              </div>
            </Col>
          );
        }

        return renderedValues.map((value, valueIndex) => {
          const fieldName = `${name}.${detailIndex}.values.${valueIndex}`;
          const isImage = isImageRequirement(detail, value);
          const fieldReadOnly = isFieldReadOnly(fieldName);

          return (
            <Col
              xs={12}
              md={isImageOnlyRequirement ? 6 : 12}
              key={fieldName}
              className="mb-2"
            >
              <div style={{ ...styles.complianceCard, height: "100%" }}>
                <strong style={styles.requirementTitle}>{requirementLabel}</strong>
                {isImage ? (
                  <div className="d-flex justify-content-center">
                    <ComplianceImageControl
                      value={value}
                      fieldName={fieldName}
                      label={requirementLabel}
                      readOnly={fieldReadOnly}
                      setFieldValue={setFieldValue}
                      acceptedFormats={acceptedFormats}
                    />
                  </div>
                ) : detail.dataType === "enum" && Array.isArray(detail.enumValues) ? (
                  <Field
                    as="select"
                    name={fieldName}
                    className="form-control form-control-sm"
                    disabled={fieldReadOnly}
                    style={styles.textValue}
                  >
                    <option value="">Select {requirementLabel}</option>
                    {detail.enumValues.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Field>
                ) : (
                  <Field
                    name={fieldName}
                    className="form-control form-control-sm"
                    readOnly={fieldReadOnly}
                    style={styles.textValue}
                  />
                )}
              </div>
            </Col>
          );
        });
      })}
    </Row>
  );
};

const KYCListingFormComplianceModal = ({
  show,
  onHide,
  modalValues,
  saveData,
  isEdit,
  readOnly,
  editableFields = [],
}) => {
  const editableSet = new Set(editableFields || []);
  const isFieldReadOnly = (path) => readOnly || !isEdit || !editableSet.has(path);

  return (
    <Modal show={show} onHide={onHide} backdrop="static" keyboard={false} size="xl">
      <Formik enableReinitialize initialValues={modalValues} onSubmit={saveData}>
        {(formik) => {
          const metadataErrors = [
            getIn(formik.errors, "vizshopId"),
            getIn(formik.errors, "lenderId"),
            getIn(formik.errors, "userId"),
          ].filter(Boolean);

          return (
            <Form autoComplete="off">
              <Modal.Header closeButton>
                <div style={styles.headerContent}>
                  <div style={styles.titleWrap}>
                    <span style={styles.titleIcon}>
                      <i className="fas fa-shield-alt"></i>
                    </span>
                    <div>
                      <Modal.Title>KYC Form</Modal.Title>
                      <p style={styles.subtitle}>Customer verification details</p>
                    </div>
                  </div>
                  <ModalHeaderBadges values={formik.values} />
                </div>
              </Modal.Header>
              <Modal.Body style={styles.modalBody}>
                {formik.submitCount > 0 && metadataErrors.length > 0 && (
                  <Alert variant="danger">{metadataErrors.join(" ")}</Alert>
                )}

                <div style={styles.mainGrid}>
                  <div style={styles.complianceColumn}>
                    <div style={styles.section}>
                      <SectionHeader title="Personal Compliance" />
                      <ComplianceSection
                        name="personalDetails.complianceDetails"
                        details={formik.values.personalDetails?.complianceDetails}
                        isFieldReadOnly={isFieldReadOnly}
                        setFieldValue={formik.setFieldValue}
                      />
                    </div>
                    <div style={{ ...styles.section, ...styles.businessComplianceSection }}>
                      <SectionHeader title="Business Compliance" />
                      <ComplianceSection
                        name="businessDetails.complianceDetails"
                        details={formik.values.businessDetails?.complianceDetails}
                        isFieldReadOnly={isFieldReadOnly}
                        setFieldValue={formik.setFieldValue}
                      />
                    </div>
                  </div>

                  <div style={styles.detailsColumn}>
                    <div style={styles.section}>
                      <SectionHeader title="Personal Information" />
                      <Row>
                        <Col xs={12} md={6}><FieldControl label="Name" name="personalDetails.name" readOnly={isFieldReadOnly("personalDetails.name")} /></Col>
                        <Col xs={12} md={6}><ContactNumberField label="Contact Number" name="personalDetails.contactNumber" readOnly={isFieldReadOnly("personalDetails.contactNumber")} /></Col>
                      </Row>
                      <Row>
                        <Col xs={12} md={6}><FieldControl label="Email" name="personalDetails.email" type="email" readOnly={isFieldReadOnly("personalDetails.email")} /></Col>
                        <Col xs={12} md={6}><FieldControl label="Date of Birth" name="personalDetails.dob" type="date" readOnly={isFieldReadOnly("personalDetails.dob")} /></Col>
                      </Row>
                      <Row>
                        <Col xs={12} md={6}><CnicField label="CNIC" name="personalDetails.cnic" readOnly={isFieldReadOnly("personalDetails.cnic")} /></Col>
                      </Row>
                    </div>
                    <div style={styles.section}>
                      <SectionHeader title="Business Information" />
                      <Row>
                        <Col xs={12} md={6}><FieldControl label="Business Name" name="businessDetails.businessName" readOnly={isFieldReadOnly("businessDetails.businessName")} /></Col>
                        <Col xs={12} md={6}>
                          <div className="form-group mb-3">
                            <label className="mb-1 small font-weight-bold text-muted">
                              Business Type
                            </label>

                            <Field
                              as="select"
                              name="businessDetails.businessType"
                              className="form-control form-control-sm"
                              disabled
                            >
                              <option value="individual">Individual</option>
                            </Field>
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12} md={6}><CnicField label="CNIC" name="businessDetails.cnic" readOnly={isFieldReadOnly("businessDetails.cnic")} /></Col>
                        <Col xs={12} md={6}><FieldControl label="Credit Limit Required" name="creditLimitRequired" type="number" readOnly={isFieldReadOnly("creditLimitRequired")} /></Col>
                      </Row>
                    </div>
                    <div style={styles.section}>
                      <SectionHeader title="Address & Coordinates" />
                      <Row>
                        <Col xs={12}><FieldControl label="Street Address" name="businessDetails.businessAddress.streetAddress" readOnly={isFieldReadOnly("businessDetails.businessAddress.streetAddress")} /></Col>
                      </Row>
                      <Row>
                        <Col xs={12} md={6}><FieldControl label="City" name="businessDetails.businessAddress.city" readOnly={isFieldReadOnly("businessDetails.businessAddress.city")} /></Col>
                        <Col xs={12} md={6}><FieldControl label="State" name="businessDetails.businessAddress.state" readOnly={isFieldReadOnly("businessDetails.businessAddress.state")} /></Col>
                      </Row>
                      <Row>
                        <Col xs={12} md={6}><FieldControl label="Country" name="businessDetails.businessAddress.country" readOnly={isFieldReadOnly("businessDetails.businessAddress.country")} /></Col>
                        <Col xs={12} md={3}><FieldControl label="Latitude" name="businessDetails.businessAddress.coordinates.lat" type="number" readOnly={isFieldReadOnly("businessDetails.businessAddress.coordinates.lat")} /></Col>
                        <Col xs={12} md={3}><FieldControl label="Longitude" name="businessDetails.businessAddress.coordinates.long" type="number" readOnly={isFieldReadOnly("businessDetails.businessAddress.coordinates.long")} /></Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Cancel</Button>
                {isEdit && editableSet.size > 0 && <Button type="submit" className="btn btn-primary">Save</Button>}
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default KYCListingFormComplianceModal;
